import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, MapPin, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './HeroSlider.css';

export default function JayrajHeroSlider() {
  const [destinations, setDestinations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev
  const navigate = useNavigate();

  // Fetch API data on mount
  useEffect(() => {
    fetch('https://admin.jayrajgroup.com/api/wedding/destinations')
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.data.length > 0) {
          setDestinations(data.data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const nextSlide = useCallback(() => {
    if (destinations.length === 0) return;
    setDirection(1); // going forward
    setCurrentIndex((prev) => (prev === destinations.length - 1 ? 0 : prev + 1));
  }, [destinations]);

  const prevSlide = () => {
    if (destinations.length === 0) return;
    setDirection(-1); // going backward
    setCurrentIndex((prev) => (prev === 0 ? destinations.length - 1 : prev - 1));
  };

  // Auto-slide every 6 seconds
  useEffect(() => {
    if (destinations.length === 0) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide, destinations]);

  if (loading) {
    return (
      <div className="jayraj-loader">
        <div className="jayraj-loader-spinner"></div>
      </div>
    );
  }

  if (destinations.length === 0) {
    return (
      <div className="jayraj-no-data">
        <p>No destinations available</p>
      </div>
    );
  }

  const current = destinations[currentIndex];

  // Slide animation variants based on direction
  const slideVariants = {
    enter: (dir) => ({
      x: dir === 1 ? '100%' : '-100%', // new slide enters from right (next) or left (prev)
    }),
    center: {
      x: 0,
    },
    exit: (dir) => ({
      x: dir === 1 ? '-100%' : '100%', // exit to left (next) or right (prev)
    }),
  };

  return (
    <section className="jayraj-hero-slider">
      {/* Background Image with Slide Animation */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={current.id}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="jayraj-slide-bg"
        >
          <img
            src={current.banner_url || current.featured_photo_url}
            alt={current.name}
            className="jayraj-slide-image"
          />
          <div className="jayraj-slide-overlay" />
        </motion.div>
      </AnimatePresence>

      {/* Top badge (commented out) */}
      {/* <div className="jayraj-top-badge">...</div> */}

      {/* Bottom Info Panel (static, no animation) */}
      <div className="jayraj-bottom-panel">
        <div className="jayraj-panel-inner">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="jayraj-info-wrapper"
            >
              {/* Location */}
              <div className="jayraj-location-badge">
                <MapPin className="jayraj-location-icon" />
                <span>
                  {current.city.name}, {current.state.name}
                </span>
              </div>

              {/* Name */}
              <h2 className="jayraj-title1">{current.name}</h2>

              {/* Short description */}
              <p className="jayraj-description">{current.short_description}</p>

              {/* Package count */}
              <div className="jayraj-package-info">
                <span>{current.packages_count} Wedding Package{current.packages_count > 1 ? 's' : ''} Available</span>
              </div>

              {/* Buttons */}
              <div className="jayraj-cta-group">
                <button
                  onClick={() => navigate('/wedding-destinations')}
                  className="jayraj-btn-primary"
                >
                  <Calendar className="jayraj-btn-icon" />
                  Plan Your Wedding
                </button>
                <button
                  onClick={() => navigate('/wedding-destinations')}
                  className="jayraj-btn-secondary"
                >
                  Explore Destinations
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="jayraj-arrows">
        <button onClick={prevSlide} className="jayraj-arrow-btn" aria-label="Previous">
          <ChevronLeft className="jayraj-arrow-icon" />
        </button>
        <button onClick={nextSlide} className="jayraj-arrow-btn" aria-label="Next">
          <ChevronRight className="jayraj-arrow-icon" />
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="jayraj-dots">
        {destinations.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`jayraj-dot ${currentIndex === index ? 'jayraj-dot-active' : 'jayraj-dot-inactive'}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}